// Quote attachments panel — the quote-level drawing pool that lives at the
// top of the Builder tab. Justin's manufacturing metaphor: a quote is a
// traveler, and the pool is the stack of drawings clipped to it. Each part
// line on the quote picks (via PartAttachmentPickerModal) which drawings
// from the pool apply to it.
//
// State model:
//   - Source of truth is `quote.attachedDrawings` (string[] of download URLs).
//     The schema field is being added by a parallel agent — we use
//     `(quote.attachedDrawings ?? [])` everywhere and cast through updateQuote
//     so this works whether the typed field exists yet or not.
//   - Uploads land in /drawings/quotes/$quoteId/ via uploadQuoteAttachment
//     and get appended to the quote's attachedDrawings via updateQuote.
//   - Remove: pops the URL from quote.attachedDrawings and best-effort-deletes
//     the Storage blob. Per the task report: we cascade — when a quote-pool
//     URL is removed, we also strip it from every part.attachedDrawings on
//     this quote so part lines don't carry dangling references. The trade-off
//     is one extra fan-out write per part touched; the alternative (leave
//     dangling) means PartAttachmentPickerModal would have to filter out
//     non-existent URLs and operators would see broken thumbnails on the
//     part record page.
//
// Layout: a card matching the Builder tab style (border-line, rounded, p-4)
// with a header row, a thumbnail grid, and a DrawingUpload below it. Empty
// state collapses the grid and shows the upload zone front-and-center.

import { useMemo, useState } from 'react';
import type { Quote } from '@/shared/crmTypes';
import { getPart, updatePart, updateQuote } from '@/shared/crm';
import {
  deleteByDownloadUrl,
  uploadQuoteAttachment,
} from '@/shared/drawingStorage';
import { PdfThumbnail } from './PdfThumbnail';
import { PdfViewerModal } from './PdfViewerModal';
import { DrawingUpload } from './DrawingUpload';

export interface QuoteAttachmentsPanelProps {
  quote: Quote;
  /** Called after upload / remove so the parent (QuoteDetailPage) refetches. */
  onChanged: () => void;
}

// Best-effort short label from a URL — last path segment, decoded, capped.
function shortName(entry: string): string {
  try {
    const u = new URL(entry);
    const last = u.pathname.split('/').filter(Boolean).pop();
    if (last) {
      const decoded = decodeURIComponent(last);
      return decoded.length > 40 ? `${decoded.slice(0, 37)}…` : decoded;
    }
  } catch {
    /* not a URL — fall through */
  }
  const parts = entry.split(/[/\\]/);
  const tail = parts[parts.length - 1] || entry;
  return tail.length > 40 ? `${tail.slice(0, 37)}…` : tail;
}

function isPdfUrl(entry: string): boolean {
  if (!/^(https?:|data:|blob:)/i.test(entry)) return false;
  const base = entry.split(/[?#]/)[0];
  return /\.pdf$/i.test(base);
}

function isImageUrl(entry: string): boolean {
  if (!/^(https?:|data:|blob:)/i.test(entry)) return false;
  const base = entry.split(/[?#]/)[0];
  return /\.(png|jpe?g|gif|webp|svg)$/i.test(base);
}

export function QuoteAttachmentsPanel({ quote, onChanged }: QuoteAttachmentsPanelProps) {
  // Use a defensive read so this works before the typed field lands on Quote
  // (parallel agent is adding it to crmTypes).
  const drawings = useMemo(
    () => (quote as Quote & { attachedDrawings?: string[] }).attachedDrawings ?? [],
    [quote],
  );

  const [viewerSource, setViewerSource] = useState<string | null>(null);
  const [viewerTitle, setViewerTitle] = useState<string | undefined>(undefined);
  const [toast, setToast] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  function flashToast(msg: string, ms = 4000) {
    setToast(msg);
    window.setTimeout(() => setToast(null), ms);
  }

  async function handleUploaded(urls: string[]) {
    if (urls.length === 0) return;
    const merged = [...drawings, ...urls];
    try {
      // Cast through Record so this compiles before the type lands on Quote.
      await updateQuote(quote.id, {
        attachedDrawings: merged,
      } as Parameters<typeof updateQuote>[1]);
      flashToast(`Added ${urls.length} drawing${urls.length === 1 ? '' : 's'} to the quote pool`);
      onChanged();
    } catch (err) {
      flashToast(`Couldn't save: ${(err as Error).message}`, 6000);
    }
  }

  async function handleRemove(url: string) {
    if (!window.confirm(
      'Remove this drawing from the quote pool?\n\nAny part on this quote that picked this drawing will lose the reference.',
    )) return;
    setWorking(true);
    try {
      // 1) Drop from quote.attachedDrawings.
      const nextDrawings = drawings.filter((d) => d !== url);
      await updateQuote(quote.id, {
        attachedDrawings: nextDrawings,
      } as Parameters<typeof updateQuote>[1]);

      // 2) Cascade: strip the URL from every part's attachedDrawings so the
      //    PartAttachmentPickerModal doesn't show a dangling entry.
      const partIds = quote.partIds ?? [];
      await Promise.all(partIds.map(async (pid) => {
        try {
          const part = await getPart(pid);
          if (!part) return;
          const partDrawings = part.attachedDrawings ?? [];
          if (!partDrawings.includes(url)) return;
          await updatePart(pid, {
            attachedDrawings: partDrawings.filter((d) => d !== url),
          });
        } catch {
          // Per-part failures are silent — the pool removal is the canonical
          // operation; a dangling part-level entry is a soft inconsistency.
        }
      }));

      // 3) Best-effort delete the Storage blob. Soft-fails (leftover blob is
      //    a soft leak, not a correctness bug).
      await deleteByDownloadUrl(url);

      flashToast('Removed from quote pool');
      onChanged();
    } catch (err) {
      flashToast(`Couldn't remove: ${(err as Error).message}`, 6000);
    } finally {
      setWorking(false);
    }
  }

  async function handleClearAll() {
    if (drawings.length === 0) return;
    if (!window.confirm(
      `Remove all ${drawings.length} drawings from the quote pool?\n\nEvery part on this quote will lose its drawing references.`,
    )) return;
    setWorking(true);
    try {
      // 1) Wipe the quote-level pool.
      await updateQuote(quote.id, {
        attachedDrawings: [],
      } as Parameters<typeof updateQuote>[1]);

      // 2) Cascade — wipe attachedDrawings on every part on the quote (only
      //    the URLs that came from the pool — but since we're clearing the
      //    whole pool, that's effectively "drop any URL that's in `drawings`").
      const poolSet = new Set(drawings);
      const partIds = quote.partIds ?? [];
      await Promise.all(partIds.map(async (pid) => {
        try {
          const part = await getPart(pid);
          if (!part) return;
          const partDrawings = part.attachedDrawings ?? [];
          const next = partDrawings.filter((d) => !poolSet.has(d));
          if (next.length === partDrawings.length) return;
          await updatePart(pid, { attachedDrawings: next });
        } catch {
          /* silent — see handleRemove */
        }
      }));

      // 3) Best-effort delete every blob.
      await Promise.all(drawings.map((u) => deleteByDownloadUrl(u)));

      flashToast('Cleared the quote pool');
      onChanged();
    } catch (err) {
      flashToast(`Couldn't clear: ${(err as Error).message}`, 6000);
    } finally {
      setWorking(false);
    }
  }

  return (
    <>
      <div className="bg-paper border border-line rounded p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <h3 className="font-semibold text-navy">
            Quote attachments{' '}
            <span className="text-text-mute font-normal">({drawings.length})</span>
          </h3>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-text-mute">
              Pool of drawings — each part below picks which apply.
            </span>
            {drawings.length > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                disabled={working}
                className="text-[11px] text-text-mute hover:text-orange disabled:opacity-40"
                title="Remove every drawing from the pool"
              >
                🗑 Clear all
              </button>
            )}
          </div>
        </div>

        {toast && (
          <div className="bg-green/10 border-l-4 border-green text-text px-3 py-2 text-xs mb-3">
            {toast}
          </div>
        )}

        {drawings.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-4">
            {drawings.map((entry, i) => {
              const label = shortName(entry);
              return (
                <div key={`${entry}-${i}`} className="flex flex-col">
                  {isPdfUrl(entry) ? (
                    <PdfThumbnail
                      source={entry}
                      width={96}
                      onClick={() => {
                        setViewerSource(entry);
                        setViewerTitle(label);
                      }}
                      className="border border-line-dark rounded p-1 hover:border-orange transition-colors"
                    />
                  ) : isImageUrl(entry) ? (
                    // Native img — no thumbnail pipeline needed for raster.
                    <button
                      type="button"
                      onClick={() => window.open(entry, '_blank', 'noopener,noreferrer')}
                      className="border border-line-dark rounded p-1 hover:border-orange transition-colors bg-white"
                      style={{ width: 96 + 8 }}
                      title={`Open ${label}`}
                    >
                      <img
                        src={entry}
                        alt={label}
                        style={{ width: 96, height: 96, objectFit: 'contain' }}
                        loading="lazy"
                      />
                    </button>
                  ) : (
                    <div
                      className="border border-line-dark rounded p-3 flex flex-col items-center justify-center text-center"
                      style={{ width: 96 + 8, minHeight: 96 * (11 / 8.5) }}
                      title={entry}
                    >
                      <div className="text-3xl mb-1">📄</div>
                      <div className="text-[10px] text-text-mute break-all line-clamp-2">
                        {label}
                      </div>
                    </div>
                  )}
                  {/* Filename + remove button */}
                  <div
                    className="mt-1 text-[10px] text-text-mute truncate text-center"
                    style={{ width: 96 + 8 }}
                    title={label}
                  >
                    {label}
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleRemove(entry)}
                    disabled={working}
                    className="mt-0.5 text-[10px] text-text-mute hover:text-orange disabled:opacity-40 self-center"
                    title="Remove from quote pool"
                  >
                    ✖ Remove
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state copy goes inside the upload zone via DrawingUpload's
            default copy; we layer the more-specific message just above it
            when the pool is empty to set context. */}
        {drawings.length === 0 && (
          <div className="text-xs text-text-mute italic mb-2">
            Drag drawings, PDFs, or CAD files here. They'll be available to
            attach to any part below.
          </div>
        )}

        <DrawingUpload
          uploader={(file) => uploadQuoteAttachment(quote.id, file)}
          onUploaded={handleUploaded}
        />
      </div>

      <PdfViewerModal
        open={viewerSource !== null}
        onClose={() => {
          setViewerSource(null);
          setViewerTitle(undefined);
        }}
        source={viewerSource}
        title={viewerTitle}
        downloadFilename={viewerTitle}
      />
    </>
  );
}
